import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Const_DifferentialDiagnosis, DBDef_Pathway, generateNewPathwayState, generateNewTransition, PathwayStateType_Assessment, PathwayStateType_Initial, PathwayTypes, PathwayTypeToLabel } from '@app/pathway-shared';
import { createSearchIntegration, Dialog_Confirmation } from '@app/styles';
import { ModuleFE_Toaster, resolveEntity } from '@nu-art/thunderstorm-frontend';
import { exists, generateHex } from '@nu-art/ts-common';
import { ModuleFE_Expression, ModuleFE_Tag, ModuleFE_Value, ModuleFE_Variable } from '@app/hcs-frontend';
import { ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '../../_entity.js';
import { Dialog_DeletePathwayState } from '../../ui/_managers/Manager_Pathway/_dialogs/Dialog_DeletePathwayState/Dialog_DeletePathwayState.js';
import { Manager_Pathway } from '../../ui/_managers/Manager_Pathway/Manager_Pathway.js';
import { guardResolutionAccordionUnsaved } from '../../ui/_managers/Manager_Pathway/renderers/shared/resolution-accordion-guard.js';
import { Dialog_DeletePathwayTransition } from '../../ui/_managers/Manager_Pathway/_dialogs/Dialog_DeletePathwayTransition/Dialog_DeletePathwayTransition.js';
import { Dialog_CopyTransition } from '../../ui/Page_PathwayEditor/selectors/renderers/renderers-pathway-transition/CopyTransitionDialog/Dialog_CopyTransition.js';
import { AccessLevel } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { SearchAddOn_ByTag } from '@app/hcs-frontend/search/index';
import { Renderer_SearchResult, SearchResult_BaseRightClickActions } from '@app/styles/frontend/ui/components/Renderer_SearchResult/Renderer_SearchResult';
import { SearchAddOn_PathwayStatus } from '../add-ons/SearchAddOn_PathwayStatus/types.js';
import './pathway.scss';
import { displayEntityDependencies } from '@app/styles/frontend/ui/consts';
import { getPathwayTimestamps } from '../../utils/pathway-timestamps.js';
const resolvePathway = resolveEntity(ModuleFE_Pathway);
const transitionableStateTypes = [PathwayStateType_Initial, PathwayStateType_Assessment];
export const canEditPathway = () => ModuleFE_Pathway.permissions.collection.edit.getAccessLevel() === AccessLevel.HasAccess;
const canDeletePathway = () => ModuleFE_Pathway.permissions.collection.delete.getAccessLevel() === AccessLevel.HasAccess;
const getPermissions = () => ({
    edit: canEditPathway(),
    delete: canDeletePathway(),
});
const { WorkHubItem, UtilObject, SearchItem } = createSearchIntegration({
    module: ModuleFE_Pathway,
    dbDef: DBDef_Pathway,
    tag: ['Pw', 'Pathway'],
    modulesToAwait: [
        ModuleFE_Pathway,
        ModuleFE_PathwayState,
        ModuleFE_PathwayTransition,
        ModuleFE_Expression,
        ModuleFE_Variable,
        ModuleFE_Value,
    ],
    ManagerComponent: Manager_Pathway,
    customOpenEditor: (item) => {
        const id = resolvePathway(item)?._id;
        if (!id)
            return;
        WorkHubItem.openTab(UtilObject.generateTabId(id), UtilObject.renderLabel(item) ?? 'New Pathway', {
            itemId: id,
            editMode: false,
        });
    },
    customMenuResolver: async (tab, workHubItem) => {
        const permissions = getPermissions();
        const pathway = resolvePathway(tab.renderArgs.itemId);
        if (!pathway)
            return [];
        const transitionToCopy = await ModuleFE_PathwayTransition.getCopiedTransition();
        const transitionOrdersToCopy = await ModuleFE_PathwayTransition.getCopiedTransitionOrders();
        const sections = [
            {
                actions: [
                    {
                        label: `${tab.renderArgs.editMode ? 'View' : 'Edit'} Mode`,
                        action: async () => {
                            if (!(await guardResolutionAccordionUnsaved(tab.id)))
                                return;
                            workHubItem.updateArgs(tab.id, { editMode: !tab.renderArgs.editMode });
                        },
                        disabled: !tab.renderArgs.itemId,
                        visible: permissions.edit && !tab.renderArgs.readonly,
                    },
                ]
            },
            {
                actions: [
                    {
                        label: 'Copy ID',
                        action: () => UtilObject.copyId(tab.renderArgs.itemId),
                        disabled: !tab.renderArgs.itemId,
                        visible: permissions.edit,
                    },
                    {
                        label: 'Copy Item',
                        action: () => UtilObject.copyObj(tab.renderArgs.itemId),
                        disabled: !tab.renderArgs.itemId,
                        visible: permissions.edit,
                    },
                    {
                        label: 'Export JSON',
                        action: () => UtilObject.exportJSON(tab.renderArgs.itemId),
                        disabled: !tab.renderArgs.itemId,
                        visible: permissions.edit,
                    }
                ]
            },
            {
                actions: [
                    {
                        label: 'Delete Pathway',
                        visible: permissions.delete,
                        action: () => {
                            Dialog_Confirmation.show({
                                header: 'Are you sure?',
                                content: () => _jsxs(_Fragment, { children: [_jsx("div", { children: "You're about to delete a pathway" }), _jsx("div", { children: pathway.label })] }),
                                buttons: [
                                    {
                                        label: 'Yes, Delete',
                                        isAsync: true,
                                        action: async () => {
                                            await ModuleFE_Pathway._v1.deletePathway({ pathwayId: pathway._id }).executeSync();
                                            workHubItem.closeTab(tab.id);
                                        }
                                    },
                                    {
                                        label: 'Don\'t Delete',
                                        isPreferred: true,
                                        action: () => {
                                        }
                                    }
                                ]
                            });
                        },
                    },
                    {
                        label: 'Add New State',
                        visible: permissions.edit,
                        action: async () => {
                            if (!(await guardResolutionAccordionUnsaved(tab.id)))
                                return;
                            const newState = generateNewPathwayState(tab.renderArgs.itemId);
                            try {
                                const state = await ModuleFE_PathwayState.v1.upsert(newState).executeSync();
                                workHubItem.updateArgs(tab.id, { stateId: state._id, transitionId: undefined });
                            }
                            catch (err) {
                                workHubItem.logError('Failed creating pathway state', err);
                            }
                        }
                    },
                    {
                        label: `${pathway.archived ? 'Un-archive' : 'Archive'} Pathway`,
                        visible: permissions.edit,
                        action: async () => {
                            await ModuleFE_Pathway._v1.togglePathwayArchiveStatus({ pathwayId: pathway._id }).executeSync();
                        }
                    },
                    {
                        label: pathway.underConstruction ? 'Remove Under Construction' : 'Mark as Under Construction',
                        visible: permissions.edit,
                        action: async () => {
                            await ModuleFE_Pathway._v1.togglePathwayUnderConstruction({ pathwayId: pathway._id }).executeSync();
                        }
                    },
                    {
                        label: 'Duplicate Pathway',
                        visible: permissions.edit,
                        innerActions: PathwayTypes
                            .map((type) => ({
                            label: `As ${PathwayTypeToLabel[type]}`,
                            action: async () => {
                                const response = await ModuleFE_Pathway._v1.duplicatePathway({
                                    pathwayId: pathway._id,
                                    targetType: type,
                                }).executeSync();
                                UtilObject_Pathway.openEditor(response.newPathway);
                            }
                        }))
                    },
                    {
                        label: 'Create Child Pathway',
                        visible: permissions.edit,
                        action: () => {
                            Dialog_Confirmation.show({
                                header: 'Create Child Pathway',
                                content: () => _jsxs(_Fragment, { children: [_jsx("div", { children: "You're about to create a child pathway from:" }), _jsx("div", { children: pathway.label })] }),
                                buttons: [
                                    {
                                        label: 'Yes, Create',
                                        isAsync: true,
                                        action: async () => {
                                            const response = await ModuleFE_Pathway._v1.createChildPathway({ pathwayId: pathway._id }).executeSync();
                                            UtilObject_Pathway.openEditor(response.pathway);
                                        }
                                    },
                                    {
                                        label: 'Cancel',
                                        isPreferred: true,
                                        action: () => {
                                        }
                                    }
                                ]
                            });
                        }
                    },
                    {
                        label: 'Paste Transition',
                        visible: permissions.edit,
                        disabled: !exists(transitionToCopy),
                        action: () => {
                            Dialog_CopyTransition.show({ pathwayId: pathway._id, transitionToCopy: transitionToCopy.data });
                        }
                    },
                ]
            },
        ];
        const state = ModuleFE_PathwayState.cache.unique(tab.renderArgs.stateId);
        if (exists(state)) {
            const otherStates = ModuleFE_PathwayState.cache.filter(s => s._id !== state._id && s.pathwayIds.includes(tab.renderArgs.itemId));
            sections.push({
                label: `State "${state.label}"`,
                actions: [
                    {
                        label: 'Delete State',
                        visible: permissions.delete,
                        action: () => {
                            Dialog_DeletePathwayState.show({
                                pathwayId: tab.renderArgs.itemId,
                                stateId: state._id,
                                onDeleteCB: () => {
                                    workHubItem.updateArgs(tab.id, { stateId: undefined });
                                }
                            });
                        }
                    },
                    {
                        label: 'Transition To',
                        visible: permissions.edit,
                        disabled: !transitionableStateTypes.includes(state.type),
                        innerActions: otherStates.map(otherState => ({
                            label: otherState.label,
                            action: async () => {
                                if (!(await guardResolutionAccordionUnsaved(tab.id)))
                                    return;
                                const newTransition = generateNewTransition(tab.renderArgs.itemId, state._id, otherState._id);
                                try {
                                    const transition = await ModuleFE_PathwayTransition.v1.upsert(newTransition).executeSync();
                                    workHubItem.updateArgs(tab.id, { transitionId: transition._id, stateId: undefined });
                                }
                                catch (err) {
                                    workHubItem.logError('Failed creating pathway transition', err);
                                }
                            }
                        }))
                    },
                ],
            });
        }
        const transition = ModuleFE_PathwayTransition.cache.unique(tab.renderArgs.transitionId);
        if (exists(transition)) {
            sections.push({
                label: `Transition "${transition.label}"`,
                actions: [
                    {
                        label: 'Delete Transition',
                        visible: permissions.delete,
                        action: async () => {
                            Dialog_DeletePathwayTransition.show({
                                pathwayId: tab.renderArgs.itemId,
                                transitionId: transition._id,
                                onDeleteCB: () => {
                                    workHubItem.updateArgs(tab.id, { transitionId: undefined });
                                }
                            });
                        }
                    },
                    {
                        label: 'Copy Transition & Orders',
                        visible: permissions.edit,
                        action: () => ModuleFE_PathwayTransition.copyTransitionToClipboard(transition),
                    },
                    {
                        label: 'Copy Transition Orders',
                        visible: permissions.edit,
                        action: () => ModuleFE_PathwayTransition.copyTransitionOrdersToClipboard(transition),
                    },
                    {
                        label: 'Paste Transition Orders',
                        visible: permissions.edit,
                        disabled: !exists(transitionOrdersToCopy) || transitionOrdersToCopy.data._id === transition._id,
                        action: async () => {
                            await ModuleFE_PathwayTransition._v1.copyTransitionOrders({
                                pathwayTransition: transitionOrdersToCopy.data,
                                targetTransitionId: transition._id
                            }).executeSync();
                            ModuleFE_Toaster.toastSuccess(`Orders pasted into '${transition.label ?? 'transition'}'`);
                        },
                    }
                ],
            });
        }
        return sections;
    },
    extraAddOnKeys: [SearchAddOn_PathwayStatus.key, SearchAddOn_ByTag.key],
    extraSearchAddOns: {
        [SearchAddOn_PathwayStatus.methodName]: (result) => {
            const item = resolvePathway(result.id);
            return { isArchived: item.archived, isUnderConstruction: item.underConstruction, type: item.type };
        },
        [SearchAddOn_ByTag.methodName]: (result) => {
            const item = resolvePathway(result.id);
            return item.tagGroups;
        }
    },
    resultSubtitle: (pathway) => pathway?.type,
    resultRenderer: (result, style) => {
        const pathway = resolvePathway(result.id);
        return _jsx(Renderer_SearchResult, { entity: pathway, entityLabel: 'Pathway', style: style, className: 'pathway', itemLabel: UtilObject.renderLabel(pathway) ?? 'N/A', subtitle: pathway?.type, extraContent: exists(pathway?.tagGroups) ? () => {
                const tags = ModuleFE_Tag.getTagsLabelByTagGroups(pathway.tagGroups);
                return tags.map(tag => _jsx("div", { className: tag.className, children: tag.label }, tag.label));
            } : undefined, timestamps: getPathwayTimestamps(result.id), onClickAction: () => UtilObject_Pathway.openEditor(pathway), rightClickActions: [
                ...SearchResult_BaseRightClickActions(pathway),
                { label: 'Check Usage', action: () => displayEntityDependencies(result.dbKey, result.id) },
            ] });
    }
});
export const WorkHubItem_Pathway = WorkHubItem;
export const SearchItem_Pathway = SearchItem;
export const UtilObject_Pathway = {
    ...UtilObject,
    openEditor: (item) => {
        const id = typeof item === 'string' ? item : (item?._id ?? `###NEWITEM###-${generateHex(8)}`);
        const resolved = typeof item === 'string' ? resolvePathway(item) : item;
        WorkHubItem.openTab(UtilObject.generateTabId(id), resolved?.label ?? 'New Pathway', {
            itemId: id,
            editMode: false,
            newItemData: id.startsWith('###NEWITEM###') ? { label: 'New Pathway', type: Const_DifferentialDiagnosis } : undefined
        });
    },
};
//# sourceMappingURL=pathway.js.map